/**
 * The territory map - anchors and prospects across every located SNF and ALF.
 *
 * Colour is the pipeline stage; an ACTIVE facility (an anchor) is drawn
 * larger with a ring so the served network reads at a glance against the
 * prospects around it. Clicking a facility opens its account.
 *
 * Leaflet directly, canvas-rendered: ~3,700 circle markers are cheap on a
 * canvas and slow as SVG nodes.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { Meta, MapPoint, Stage, bdApi, STAGE_LABEL, STAGE_TONE } from './bdApi';
import { NativeSelect, StateLine } from './bdUi';

const FLORIDA_CENTER: L.LatLngExpression = [28.1, -82.2];
const FLORIDA_ZOOM = 6.4;

const esc = (v: unknown) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export default function BdMapView({ meta, onOpen, refreshKey }: { meta: Meta; onOpen: (id: string) => void; refreshKey: number }) {
  const [type, setType] = useState('ALL');
  const [owner, setOwner] = useState('all');
  const [shownStages, setShownStages] = useState<Set<Stage>>(() => new Set(meta.stages.map((s) => s.key).filter((s) => s !== 'DO_NOT_CONTACT')));
  const [data, setData] = useState<{ points: MapPoint[]; not_located: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const openRef = useRef(onOpen);
  openRef.current = onOpen;

  useEffect(() => {
    let live = true;
    setError(null);
    bdApi.map(type).then((r) => {
      if (!live) return;
      if (r.success) setData(r.data); else setError(r.error || 'The map could not be loaded.');
    });
    return () => { live = false; };
  }, [type, refreshKey]);

  const points = useMemo(() => (data?.points || []).filter((p) =>
    shownStages.has(p.stage)
    && (owner === 'all' || (owner === 'mine' ? p.owner_user_id === meta.me.id : owner === 'unassigned' ? !p.owner_user_id : p.owner_user_id === owner))
  ), [data, shownStages, owner, meta.me.id]);

  const counts = useMemo(() => {
    const c: Partial<Record<Stage, number>> = {};
    (data?.points || []).forEach((p) => { c[p.stage] = (c[p.stage] || 0) + 1; });
    return c;
  }, [data]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { preferCanvas: true, zoomSnap: 0.1 }).setView(FLORIDA_CENTER, FLORIDA_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 18,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; };
  }, []);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    // Anchors last, so they sit on top of the prospects around them.
    const ordered = [...points].sort((a, b) => Number(a.stage === 'ACTIVE') - Number(b.stage === 'ACTIVE'));
    for (const p of ordered) {
      const anchor = p.stage === 'ACTIVE';
      const color = STAGE_TONE[p.stage].dot;
      const m = L.circleMarker([p.lat, p.lng], {
        radius: anchor ? 7 : 4.5,
        color: anchor ? '#ffffff' : color,
        weight: anchor ? 2 : 1,
        fillColor: color,
        fillOpacity: anchor ? 0.95 : 0.7,
      });
      m.bindTooltip(
        `<b>${esc(p.name)}</b><br>${esc([p.city, p.type, p.beds ? `${p.beds} beds` : null].filter(Boolean).join(' · '))}` +
        `<br>${esc(STAGE_LABEL[p.stage])}${p.owner_name ? ` · ${esc(p.owner_name)}` : ''}`,
        { direction: 'top', offset: [0, -4] }
      );
      m.on('click', () => openRef.current(p.id));
      m.addTo(layer);
    }
  }, [points]);

  const toggle = (s: Stage) => setShownStages((cur) => { const n = new Set(cur); if (n.has(s)) n.delete(s); else n.add(s); return n; });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <NativeSelect label="Facilities" value={type} onChange={setType}>
          <option value="ALL">SNFs + ALFs</option>
          <option value="SNF">SNFs</option>
          <option value="ALF">ALFs</option>
        </NativeSelect>
        <NativeSelect label="Owner" value={owner} onChange={setOwner}>
          <option value="all">Everyone</option>
          <option value="mine">Mine</option>
          <option value="unassigned">Unassigned</option>
          {meta.reps.filter((r) => r.id !== meta.me.id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </NativeSelect>
        <fieldset className="flex flex-wrap gap-1.5">
          <legend className="text-xs text-muted-foreground mb-1 w-full">Show stages</legend>
          {meta.stages.map((s) => {
            const on = shownStages.has(s.key);
            return (
              <button key={s.key} type="button" aria-pressed={on} onClick={() => toggle(s.key)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${on ? 'bg-background' : 'opacity-45 bg-muted'}`}>
                <span aria-hidden className="h-2.5 w-2.5 rounded-full" style={{ background: STAGE_TONE[s.key].dot }} />
                {STAGE_LABEL[s.key]}
                <span className="text-muted-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{counts[s.key] || 0}</span>
              </button>
            );
          })}
        </fieldset>
      </div>

      <StateLine error={error} />
      <div className="relative rounded-lg border overflow-hidden">
        <div ref={containerRef} style={{ height: 620 }} aria-label="Map of facilities by pipeline stage" role="region" />
        {!data && !error && <p role="status" className="absolute inset-0 grid place-items-center text-sm text-muted-foreground bg-background/60">Loading the map…</p>}
      </div>
      {data && (
        <p className="text-xs text-muted-foreground">
          {points.length.toLocaleString()} shown of {data.points.length.toLocaleString()} located.
          {data.not_located > 0 && ` ${data.not_located.toLocaleString()} facilities have no usable location yet and are not on the map.`}
          {' '}Larger ringed dots are active (anchor) facilities.
        </p>
      )}
    </div>
  );
}
